import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LockResetIcon from '@mui/icons-material/LockReset';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useAuth } from '../../services/cognito';
import config from '../../config';
import axios from 'axios';
import TerritoryReset from './TerritoryReset';

interface CognitoUser {
  username: string;
  email: string;
  name?: string;
  givenName?: string;
  familyName?: string;
  status: string;
  enabled: boolean;
  created: string;
  isAdmin: boolean;
}

const AdminPage: React.FC = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState<CognitoUser[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetTarget, setResetTarget] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const fetchUsers = useCallback(async (searchQuery?: string) => {
    if (!token) return;
    setLoading(true);
    try {
      const params = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : '';
      const res = await axios.get(`${config.api.url}/mdsouth/admin/users${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(res.data.users);
    } catch (err) {
      setSnackbar({ open: true, message: '사용자 목록을 불러오지 못했습니다', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = () => {
    fetchUsers(search);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleToggleAdmin = async (username: string, currentlyAdmin: boolean) => {
    if (!token) return;
    const action = currentlyAdmin ? 'revoke-admin' : 'grant-admin';
    try {
      await axios.post(
        `${config.api.url}/mdsouth/admin/users/${encodeURIComponent(username)}/${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSnackbar({
        open: true,
        message: currentlyAdmin
          ? `"${username}" 관리자 권한이 해제되었습니다`
          : `"${username}" 관리자 권한이 부여되었습니다`,
        severity: 'success',
      });
      fetchUsers(search || undefined);
    } catch (err: any) {
      const msg = err?.response?.data?.error || '관리자 권한 변경에 실패했습니다';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  const handleResetPassword = async () => {
    if (!resetTarget || !token) return;
    try {
      await axios.post(
        `${config.api.url}/mdsouth/admin/users/${encodeURIComponent(resetTarget)}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSnackbar({ open: true, message: `"${resetTarget}" 비밀번호가 12341234로 리셋되었습니다`, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: '비밀번호 리셋에 실패했습니다', severity: 'error' });
    } finally {
      setResetTarget(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        사용자 관리
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, mb: 3, maxWidth: 500 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="사용자명으로 검색..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="contained" onClick={handleSearch} disabled={loading}>
          검색
        </Button>
        <Button variant="outlined" onClick={() => { setSearch(''); fetchUsers(); }} disabled={loading}>
          전체
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>사용자명</strong></TableCell>
              <TableCell><strong>이메일</strong></TableCell>
              <TableCell><strong>이름</strong></TableCell>
              <TableCell><strong>상태</strong></TableCell>
              <TableCell align="center"><strong>권한</strong></TableCell>
              <TableCell align="center"><strong>작업</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.username} hover>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.name || user.familyName || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={user.status === 'CONFIRMED' ? '확인됨' : '미확인'}
                    color={user.status === 'CONFIRMED' ? 'success' : 'warning'}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="center">
                  <Button
                    size="small"
                    variant={user.isAdmin ? 'contained' : 'outlined'}
                    color={user.isAdmin ? 'info' : 'inherit'}
                    startIcon={<AdminPanelSettingsIcon />}
                    onClick={() => handleToggleAdmin(user.username, user.isAdmin)}
                  >
                    {user.isAdmin ? '관리자' : '일반'}
                  </Button>
                </TableCell>
                <TableCell align="center">
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<LockResetIcon />}
                    onClick={() => setResetTarget(user.username)}
                  >
                    비밀번호 리셋
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {search ? '검색 결과가 없습니다' : '사용자가 없습니다'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        총 {users.length}명
      </Typography>

      {/* 구역 리셋 관리 */}
      <TerritoryReset />

      {/* 비밀번호 리셋 확인 다이얼로그 */}
      <Dialog open={!!resetTarget} onClose={() => setResetTarget(null)}>
        <DialogTitle>비밀번호 리셋</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>&ldquo;{resetTarget}&rdquo;</strong> 의 비밀번호를 <strong>12341234</strong>로 리셋하시겠습니까?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetTarget(null)}>취소</Button>
          <Button onClick={handleResetPassword} color="warning" variant="contained">
            리셋
          </Button>
        </DialogActions>
      </Dialog>

      {/* 결과 알림 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminPage;
